import { Capacitor } from '@capacitor/core';
import { isInstalledApp } from '@/lib/appUrl';
import React, { useState } from 'react';
import { useNotifications } from '@/contexts/NotificationsContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Bell, Check, CheckCheck, Trophy, Calendar, Users,
  Megaphone, UserPlus, MessageSquare, HelpCircle,
  BellRing, Share2, PlusSquare, Moon, Smartphone,
} from 'lucide-react';
import { formatDate, getRelativeTime } from '@/lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const NOTIFICATION_ICONS = {
  match_invite: Calendar,
  league_invite: Trophy,
  match_reminder: Calendar,
  league_request: UserPlus,
  league_join_request: UserPlus,
  announcement: Megaphone,
  match_result: Calendar,
  match_payment: Calendar,
  collector_request: Calendar,
  teams_confirmed: Users,
  match_mvp: Trophy,
  feedback: MessageSquare,
  facility_booking: Calendar,
  default: Bell
};

export const NotificationBell = () => {
  const native = Capacitor.isNativePlatform();
  const {
    notifications, unreadCount, markAsRead, markAllAsRead, loading,
    pushPermission, pushSupported, requestPushPermission,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      const result = await requestPushPermission();
      if (result.ok) {
        toast.success(native ? 'Promemoria MVP attivati' : 'Notifiche push attivate');
      } else {
        const messages = {
          unsupported: 'Browser non supporta le notifiche push.',
          denied: native ? 'Abilita le notifiche di LeagueHub nelle impostazioni del telefono.' : 'Permesso negato. Sbloccale dalle impostazioni del browser.',
          'sw-failed': 'Service Worker non registrato. Ricarica la pagina.',
          'no-vapid': 'Configurazione push non disponibile lato server.',
          'subscribe-failed':
            "Errore nell'iscrizione. Potrebbe essere bloccato dal browser/SO.",
          'server-error': 'Errore di rete: iscrizione non salvata sul server.',
        };
        toast.error('Notifiche non attivate', {
          description: messages[result.reason] || `Errore: ${result.reason}`,
        });
      }
    } catch (e) {
      toast.error('Errore inatteso', {
        description: e?.message || String(e),
      });
    } finally {
      setPushBusy(false);
    }
  };

  const getIcon = (type) => {
    const Icon = NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
    return <Icon className="h-4 w-4" />;
  };

  const getNotificationLink = (notification) => {
    switch (notification.type) {
      case 'match_mvp':
        return `/matches/${notification.reference_id}#mvp`;
      case 'collector_request':
        return `/matches/${notification.reference_id}?collega=stripe`;
      case 'match_invite':
      case 'match_reminder':
      case 'match_result':
      case 'match_payment':
      case 'teams_confirmed':
        return `/matches/${notification.reference_id}`;
      case 'league_invite':
      case 'league_request':
      case 'league_join_request':
      case 'league_join_approved':
      case 'league_joined':
      case 'league_member_joined':
        return `/leagues/${notification.reference_id}`;
      case 'announcement':
        return '/search?tab=announcements';
      case 'facility_booking':
        return `/facilities/${notification.reference_id}/dashboard#bookings`;
      default:
        return null;
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell" aria-label="Notifiche"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-accent text-accent-foreground text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="notification-popover p-0" align="end" collisionPadding={8}>
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold">Notifiche</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Segna tutte lette
            </Button>
          )}
        </div>

        {pushSupported && pushPermission === 'default' && (
          <div className="p-3 border-b bg-primary/5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-xs text-muted-foreground">
                {native ? 'Ricevi il promemoria per votare l’MVP' : 'Ricevi notifiche push sul tuo dispositivo'}
              </p>
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="text-muted-foreground/70 hover:text-foreground shrink-0"
                aria-label="Come attivare le notifiche"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
            </div>
            <Button size="sm" onClick={handleEnablePush} disabled={pushBusy}>
              {pushBusy ? '...' : 'Attiva'}
            </Button>
          </div>
        )}
        {pushSupported && pushPermission === 'denied' && (
          <div className="p-3 border-b bg-muted/30 flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {native ? 'Abilita le notifiche di LeagueHub nelle impostazioni del telefono.' : 'Push bloccate. Abilitale dalle impostazioni del browser per riceverle.'}
            </p>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="text-muted-foreground/70 hover:text-foreground shrink-0"
              aria-label="Come attivare le notifiche"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {pushSupported && pushPermission === 'granted' && (
          <div className="p-3 border-b bg-primary/5 flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Push attive su questo dispositivo.
            </p>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="text-muted-foreground/70 hover:text-foreground shrink-0"
              aria-label="Non ricevo le notifiche"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nessuna notifica</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.slice(0, 20).map((notification) => {
                const link = getNotificationLink(notification);
                const content = (
                  <div 
                    className={`p-3 hover:bg-muted/50 transition-colors ${
                      !notification.read ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => {
                      if (!notification.read) {
                        markAsRead(notification.notification_id);
                      }
                      setOpen(false);
                    }}
                  >
                    <div className="flex gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                        !notification.read ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        {getIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {getRelativeTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />
                      )}
                    </div>
                  </div>
                );

                return link ? (
                  <Link key={notification.notification_id} to={link}>
                    {content}
                  </Link>
                ) : (
                  <div key={notification.notification_id}>
                    {content}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>

    <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-primary" />
            {native ? 'Attiva i promemoria MVP' : 'Attiva le notifiche push'}
          </DialogTitle>
          <DialogDescription>
            {native ? 'Il telefono ti ricorda di votare alla fine dell’ora di gioco, anche con l’app chiusa. Gli orari si aggiornano quando apri l’app.' : 'Ricevi promemoria partite, inviti e risultati anche con il browser chiuso.'}
          </DialogDescription>
        </DialogHeader>

        {/* Mock visivo del prompt browser/iOS */}
        <div className="rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-background p-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <BellRing className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">
              {native ? 'LeagueHub vuole inviarti notifiche' : 'leaguehub.it vuole inviarti notifiche'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Clicca <b>Consenti</b> quando ti viene chiesto.
            </p>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex gap-3">
            <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 text-xs font-semibold">1</div>
            <div className="flex-1">
              <p className="font-medium flex items-center gap-1.5">
                <Bell className="h-4 w-4" /> Clicca "Attiva" qui sopra
              </p>
              <p className="text-xs text-muted-foreground">
                Ti verrà chiesto il permesso. Conferma con <b>Consenti</b>.
              </p>
            </div>
          </div>
          {!isInstalledApp() && (
          <div className="flex gap-3">
            <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 text-xs font-semibold">2</div>
            <div className="flex-1">
              <p className="font-medium flex items-center gap-1.5">
                <Smartphone className="h-4 w-4" /> Su iPhone: installa la PWA
              </p>
              <p className="text-xs text-muted-foreground">
                Safari → <Share2 className="inline h-3 w-3 mx-0.5" /> Condividi → <PlusSquare className="inline h-3 w-3 mx-0.5" /> "Aggiungi alla schermata Home", poi apri l'icona da home.{' '}
                {!isInstalledApp() && <Link to="/install-pwa" onClick={() => { setHelpOpen(false); setOpen(false); }} className="text-primary underline">Guida completa</Link>}
              </p>
            </div>
          </div>
          )}
          <div className="flex gap-3">
            <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 text-xs font-semibold">3</div>
            <div className="flex-1">
              <p className="font-medium flex items-center gap-1.5">
                <Moon className="h-4 w-4" /> Controlla "Non disturbare"
              </p>
              <p className="text-xs text-muted-foreground">
                Se il telefono o Mac ha <b>Focus / Non disturbare</b> attivo, le push arrivano ma restano silenziose nel Centro Notifiche.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setHelpOpen(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default NotificationBell;
